import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, assetUrls } from '../lib/api.js';
import { useSession, TIER } from '../App.js';

type Cluster = Awaited<ReturnType<typeof api.listFaceClusters>>['clusters'][number];

/**
 * Naming and correcting the groups the vision worker finds.
 *
 * Clustering is deliberately conservative, so the same person often appears as
 * several groups. Merging is therefore the common operation, not an edge case,
 * and it gets equal billing with naming.
 */
export function PeoplePage(): ReactNode {
  const { me } = useSession();
  const canLabel = (me?.tier ?? 0) >= TIER.editor;

  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [filter, setFilter] = useState<'all' | 'named' | 'unnamed'>('all');
  // The threshold hides one-off detections of passers-by, but on a small or
  // new archive it can hide everyone -- including the only way to name them.
  const [minFaces, setMinFaces] = useState(2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rebuilding, setRebuilding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listFaceClusters(filter, minFaces)
      .then((r) => setClusters(r.clusters))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filter, minFaces]);

  useEffect(load, [load]);

  const saveName = async (id: string): Promise<void> => {
    try {
      await api.nameFaceCluster(id, draftName.trim() || null);
      setEditing(null);
      setDraftName('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that name');
    }
  };

  const mergeSelected = async (targetId: string): Promise<void> => {
    const sources = [...selected].filter((id) => id !== targetId);
    if (sources.length === 0) return;

    const target = clusters.find((c) => c.id === targetId);
    const name = target?.label ?? 'this group';
    if (!confirm(`Merge ${sources.length} other group(s) into ${name}? This cannot be undone automatically.`)) {
      return;
    }

    try {
      const result = await api.mergeFaceClusters(targetId, sources);
      setSelected(new Set());
      load();
      setError(null);
      alert(`Merged ${result.facesMoved} face(s).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    }
  };

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const named = clusters.filter((c) => c.label).length;

  return (
    <div className="body no-sidebar">
      <div className="main" style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <h2>People</h2>
        <p style={{ color: 'var(--text-dim)', marginTop: -8 }}>
          Faces are grouped automatically. Name a group and everyone in it becomes
          searchable — the name applies to every photo and video they appear in, including
          ones added later.
        </p>

        {error && <div className="notice error">{error}</div>}

        <div className="toolbar">
          {(['all', 'unnamed', 'named'] as const).map((value) => (
            <button
              key={value}
              className={`btn${filter === value ? ' primary' : ''}`}
              onClick={() => setFilter(value)}
              style={{ textTransform: 'capitalize' }}
            >
              {value}
            </button>
          ))}
          <div className="spacer" />
          <label
            className="flex"
            style={{ fontSize: 12, color: 'var(--text-dim)', gap: 6 }}
            title="Groups with only one detected face are usually a passer-by, so they are hidden by default"
          >
            <input
              type="checkbox"
              checked={minFaces === 1}
              onChange={(e) => setMinFaces(e.target.checked ? 1 : 2)}
              style={{ width: 'auto' }}
            />
            include one-off detections
          </label>
          <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
            {named} named of {clusters.length} shown
          </span>
        </div>

        {canLabel && (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="flex" style={{ flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <strong style={{ fontSize: 13 }}>Groups look wrong?</strong>
                <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  Grouping is decided once per face when media is indexed and never
                  revisited, so improvements only reach new uploads. Rebuilding re-runs it
                  over everything already stored. No re-analysis — the face data exists
                  already, so this takes seconds, and names are carried across.
                </div>
              </div>
              <button
                className="btn"
                disabled={rebuilding}
                onClick={() => {
                  setNotice(null);
                  void api
                    .previewClustering()
                    .then((r) =>
                      setNotice(
                        `${r.faces} face(s) would form ${r.wouldBeClusters} group(s); ` +
                          `there are ${r.currentClusters} now. Nothing has changed — preview only.`,
                      ),
                    )
                    .catch((err: Error) => setError(err.message));
                }}
              >
                Preview
              </button>
              <button
                className="btn primary"
                disabled={rebuilding}
                onClick={() => {
                  const warning = [
                    'Rebuild every person group from scratch?',
                    '',
                    'Names are matched back onto the rebuilt groups, but any merges you',
                    'made by hand are redone from the face data.',
                  ].join(String.fromCharCode(10));
                  if (!confirm(warning)) {
                    return;
                  }
                  setRebuilding(true);
                  setNotice(null);
                  void api
                    .rebuildFaceClusters()
                    .then((r) => {
                      let message =
                        `Rebuilt ${r.facesProcessed} face(s) into ${r.clustersAfter} group(s) ` +
                        `(was ${r.clustersBefore}). ${r.namesPreserved} name(s) kept.`;
                      if (r.namesLost.length > 0) {
                        message +=
                          ` Could not re-match: ${r.namesLost.join(', ')} — those people no ` +
                          'longer form a distinct group and need naming again.';
                      }
                      setNotice(message);
                      load();
                    })
                    .catch((err: Error) => setError(err.message))
                    .finally(() => setRebuilding(false));
                }}
              >
                {rebuilding ? 'Rebuilding…' : 'Rebuild groups'}
              </button>
            </div>
            {notice && (
              <div className="notice success" style={{ marginTop: 10, marginBottom: 0 }}>
                {notice}
              </div>
            )}
          </div>
        )}

        {selected.size > 1 && canLabel && (
          <div className="notice">
            <strong>{selected.size} groups selected.</strong> These are all the same person?
            Choose which one to keep — the others merge into it and their name is discarded.
            <div className="flex" style={{ marginTop: 8, flexWrap: 'wrap' }}>
              {[...selected].map((id) => {
                const cluster = clusters.find((c) => c.id === id);
                return (
                  <button key={id} className="btn sm" onClick={() => void mergeSelected(id)}>
                    Keep {cluster?.label ?? 'unnamed'} ({cluster?.face_count ?? 0})
                  </button>
                );
              })}
              <button className="btn sm" onClick={() => setSelected(new Set())}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && clusters.length === 0 && <div className="empty">Loading…</div>}

        {!loading && clusters.length === 0 && (
          <div className="empty">
            <p>No groups of faces yet.</p>
            <p style={{ fontSize: 12 }}>
              {minFaces > 1
                ? 'People detected in only one photo are hidden. Tick "include one-off ' +
                  'detections" above to see them — on a small archive that is most of them.'
                : 'Face grouping needs the optional requirements-faces.txt dependencies and ' +
                  'ENABLE_FACES=true. Groups appear as media is indexed.'}
            </p>
          </div>
        )}

        <div className="grid">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              className={`card${selected.has(cluster.id) ? ' selected' : ''}`}
            >
              {canLabel && (
                <input
                  type="checkbox"
                  className="select-box"
                  checked={selected.has(cluster.id)}
                  onChange={() => toggle(cluster.id)}
                  aria-label={`Select ${cluster.label ?? 'unnamed group'}`}
                  title="Select to merge with another group"
                />
              )}

              <Link to={`/?faceClusterId=${cluster.id}`} title="See everything this person is in">
                {cluster.cover_asset_id ? (
                  <img
                    className="thumb"
                    src={assetUrls.thumb(cluster.cover_asset_id)}
                    alt={cluster.label ?? 'Unnamed person'}
                    loading="lazy"
                  />
                ) : (
                  <div className="thumb placeholder">🙂</div>
                )}
              </Link>

              <div className="meta">
                {editing === cluster.id ? (
                  <div className="flex">
                    <input
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveName(cluster.id);
                        if (e.key === 'Escape') setEditing(null);
                      }}
                      placeholder="Their name…"
                      maxLength={80}
                    />
                    <button className="btn sm primary" onClick={() => void saveName(cluster.id)}>
                      Save
                    </button>
                  </div>
                ) : (
                  <div
                    className="name"
                    style={{
                      cursor: canLabel ? 'pointer' : 'default',
                      color: cluster.label ? undefined : 'var(--text-dim)',
                    }}
                    onClick={() => {
                      if (!canLabel) return;
                      setEditing(cluster.id);
                      setDraftName(cluster.label ?? '');
                    }}
                    title={canLabel ? 'Click to rename' : undefined}
                  >
                    {cluster.label ?? 'Unnamed person'}
                    {canLabel && ' ✎'}
                  </div>
                )}

                <div className="sub">
                  <span>{cluster.asset_count} items</span>
                  <span>{cluster.face_count} faces</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!canLabel && clusters.length > 0 && (
          <div className="notice" style={{ marginTop: 16 }}>
            You need editor permissions to name people. Ask a lead to grant it.
          </div>
        )}
      </div>
    </div>
  );
}
